import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import { RequestCommentMapper } from '../lib/request-comments.mapper'
import { RequestsService } from '../lib/requests.service'
import { RequestByIdApiType, RequestType } from '../lib/requests.types'
import { REQUEST_BY_ID_RQ_KEY } from './useRequestById'
import { REQUESTS_LIST_RQ_KEY } from './useRequestsList'

export const REQUEST_COMMENTS_QUERY_KEY = 'request-comments-list'

interface Props {
  requestId: string
  pageSize?: number
}

export const useGetRequestCommentsList = ({
  requestId,
  pageSize = 10
}: Props) => {
  const queryClient = useQueryClient()

  return useInfiniteQuery({
    queryKey: [REQUEST_COMMENTS_QUERY_KEY, requestId],
    queryFn: async ({ pageParam }) => {
      const { data, page, total } =
        await RequestsService.getRequestCommentsList({
          id: requestId,
          page: pageParam,
          pageSize
        })

      queryClient.setQueriesData<{ data: RequestType[] }>(
        { queryKey: [REQUESTS_LIST_RQ_KEY] },
        cachedData => {
          if (!cachedData) {
            return cachedData
          }

          return {
            ...cachedData,
            data: cachedData.data.map((request: RequestType) => {
              return request.guid === requestId
                ? { ...request, comments_count: 0 }
                : request
            })
          }
        }
      )

      queryClient.setQueryData<{
        data: RequestByIdApiType
      }>([REQUEST_BY_ID_RQ_KEY, requestId], cachedData => {
        if (!cachedData) {
          return cachedData
        }

        return {
          ...cachedData,
          data: { ...cachedData.data, unreadComments: 0 }
        }
      })

      return {
        response:
          data?.map(rawComment => ({
            ...RequestCommentMapper.toEntity(rawComment)
          })) || [],
        page: total - page * pageSize > 0 ? pageParam : undefined
      }
    },
    enabled: !!requestId,
    staleTime: 0,
    select: response =>
      response.pages
        .flatMap(page => page.response)
        .filter(item => item !== null),
    initialPageParam: 1,
    getPreviousPageParam: lastPage =>
      lastPage.page === 0 ? lastPage.page - 1 : undefined,
    getNextPageParam: firstPage =>
      firstPage.page ? firstPage.page + 1 : undefined
  })
}
